<template>
  <v-card :loading="isLoading">
    <v-card-text>
      <v-data-table
        v-bind="dataTableAttrs"
        :headers="headersShown"
        :items="list"
        :loading="isLoading"
        :options.sync="options"
        :server-items-length="total"
        :items-per-page.sync="itemsPerPage"
        v-model="selectedItems"
        @update:items-per-page="handleItemsPerPageUpdated"
        :show-select="false"
        :item-class="getRowClass"
      >
        <template v-slot:top>
          <rs-table-title-bar
            v-if="$refs.headSearch"
            title="TYA Ödeme Hazırlığı"
            icon="mdi-credit-card-check-outline"
            :add-route="null"
            @clear-filters="handleClearFilters"
            @reload="loadList"
            hide-edit
            :search.sync="search.query"
            v-bind="titleBarAttrs"
          />

          <v-row dense class="mt-2 mb-2">
            <v-col cols="12" md="3">
              <div class="text-caption text--secondary mb-1">
                Provider Seçimi
              </div>
              <v-btn-toggle
                v-model="search.has_selected_provider"
                dense
                color="primary"
              >
                <v-btn :value="null" small>Tümü</v-btn>
                <v-btn :value="true" small>Seçili</v-btn>
                <v-btn :value="false" small>Eksik</v-btn>
              </v-btn-toggle>
            </v-col>

            <v-col cols="12" md="3">
              <v-select
                v-model="search.provider_setup_status"
                :items="providerSetupStatusOptions"
                item-text="text"
                item-value="id"
                label="Entegrasyon Durumu"
                dense
                clearable
                hide-details="auto"
              />
            </v-col>

            <v-col cols="6" sm="6" md="3">
              <v-text-field
                v-model="search.payment_created_at.min"
                label="Ödeme Başlangıç"
                type="date"
                dense
                hide-details="auto"
                clearable
              />
            </v-col>

            <v-col cols="6" sm="6" md="3">
              <v-text-field
                v-model="search.payment_created_at.max"
                label="Ödeme Bitiş"
                type="date"
                dense
                hide-details="auto"
                clearable
              />
            </v-col>
          </v-row>

          <v-row dense class="mt-4 mb-4">
            <v-col cols="6" sm="4" md="2">
              <v-sheet outlined class="pa-2 text-center">
                <div class="caption mb-1">Toplam TYA</div>
                <div class="subtitle-1 font-weight-bold">
                  {{ totals.itemCount }}
                </div>
              </v-sheet>
            </v-col>

            <v-col cols="6" sm="4" md="2">
              <v-sheet outlined class="pa-2 text-center">
                <div class="caption mb-1">Provider Seçili</div>
                <div class="subtitle-1 font-weight-bold">
                  {{ totals.selectedProviderCount }}
                </div>
              </v-sheet>
            </v-col>

            <v-col cols="6" sm="4" md="2">
              <v-sheet outlined class="pa-2 text-center">
                <div class="caption mb-1">Re-card Gerekli</div>
                <div class="subtitle-1 font-weight-bold">
                  {{ totals.recardRequiredUserCount }}
                </div>
              </v-sheet>
            </v-col>

            <v-col cols="6" sm="4" md="2">
              <v-sheet outlined class="pa-2 text-center">
                <div class="caption mb-1">Talimat Riski</div>
                <div class="subtitle-1 font-weight-bold">
                  {{ totals.subscriptionMissingCardCount }}
                </div>
              </v-sheet>
            </v-col>

            <v-col cols="6" sm="4" md="2">
              <v-sheet outlined class="pa-2 text-center">
                <div class="caption mb-1">Bekleyen/Unknown</div>
                <div class="subtitle-1 font-weight-bold">
                  {{ totals.pendingUnknownAttemptCount }}
                </div>
              </v-sheet>
            </v-col>

            <v-col cols="6" sm="4" md="2">
              <v-sheet outlined class="pa-2 text-center">
                <div class="caption mb-1">Başarılı Hacim</div>
                <div class="subtitle-1 font-weight-bold">
                  <rs-table-cell-number
                    price
                    :value="totals.successPaymentAmount"
                  />
                </div>
              </v-sheet>
            </v-col>
          </v-row>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:body.prepend="{ headers }">
          <rs-table-head-search
            :headers="headers"
            :search.sync="search"
            ref="headSearch"
          />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:body.append="{ headers }">
          <rs-table-foot-totals
            :headers="headers"
            :totals="footTotals"
            v-if="list.length > 0"
          >
            <template #foot.success_payment_amount>
              <rs-table-cell-number
                price
                :value="footTotals.success_payment_amount"
              />
            </template>
          </rs-table-foot-totals>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.actions="{ item }">
          <v-btn icon small @click="showDetail(item)">
            <v-icon small>mdi-eye-outline</v-icon>
          </v-btn>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.payment_readiness_status="{ item }">
          <v-chip
            x-small
            dark
            :color="readinessStatusMeta(item.payment_readiness_status).color"
          >
            {{ readinessStatusMeta(item.payment_readiness_status).text }}
          </v-chip>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.cluster_name="{ item }">
          <router-link
            :to="{
              name: 'definitions.clusters.edit',
              params: { id: item.cluster_id },
              hash: '#cc',
            }"
          >
            {{ item.cluster_name }}
          </router-link>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.provider_setup_status="{ value }">
          {{ setupStatusText(value) }}
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.active_provider_card_coverage_rate="{ value }">
          {{ percent(value) }}
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.active_provider_subscription_coverage_rate="{ value }">
          {{ percent(value) }}
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.success_payment_count="{ item, value }">
          <router-link
            v-if="Number(value) > 0"
            :to="ccPaymentRoute(item, { is_success: true })"
          >
            {{ value }}
          </router-link>
          <span v-else>0</span>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.failed_payment_count="{ item, value }">
          <router-link
            v-if="Number(value) > 0"
            :to="ccPaymentRoute(item, { is_success: false })"
          >
            {{ value }}
          </router-link>
          <span v-else>0</span>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.success_payment_amount="{ value }">
          <rs-table-cell-number price :value="value" />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.last_success_at="{ value }">
          <span>
            {{ value ? new Date(value).toLocaleString("tr-TR") : "-" }}
          </span>
        </template>
      </v-data-table>
    </v-card-text>

    <v-navigation-drawer
      v-model="detailDrawer"
      temporary
      fixed
      right
      width="520"
    >
      <v-card flat v-if="detailItem">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-credit-card-check-outline</v-icon>
          <span>{{ detailItem.cluster_name }}</span>
          <v-spacer />
          <v-btn icon @click="detailDrawer = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text>
          <div class="mb-4">
            <div class="text-caption text--secondary">Genel Durum</div>
            <v-chip
              small
              dark
              :color="
                readinessStatusMeta(detailItem.payment_readiness_status).color
              "
            >
              {{
                readinessStatusMeta(detailItem.payment_readiness_status).text
              }}
            </v-chip>
            <div class="mt-2">
              {{ readinessReasonText(detailItem.payment_readiness_reason) }}
            </div>
          </div>

          <v-simple-table dense>
            <tbody>
              <tr>
                <th>Yönetim Şirketi</th>
                <td>{{ detailItem.company_name || "-" }}</td>
              </tr>
              <tr>
                <th>Provider</th>
                <td>{{ detailItem.payment_provider_name || "-" }}</td>
              </tr>
              <tr>
                <th>Entegrasyon Durumu</th>
                <td>{{ setupStatusText(detailItem.provider_setup_status) }}</td>
              </tr>
              <tr>
                <th>Submerchant</th>
                <td>
                  {{
                    detailItem.provider_needs_submerchant
                      ? detailItem.submerchant_id || "Eksik"
                      : "Gerekmiyor"
                  }}
                </td>
              </tr>
              <tr>
                <th>Hakediş IBAN</th>
                <td>{{ detailItem.payout_account_iban || "Eksik" }}</td>
              </tr>
              <tr>
                <th>Kart Kapsamı</th>
                <td>
                  {{ detailItem.active_provider_card_user_count }} /
                  {{ detailItem.card_user_any_provider_count }}
                  ({{ percent(detailItem.active_provider_card_coverage_rate) }})
                </td>
              </tr>
              <tr>
                <th>Otomatik Talimat</th>
                <td>
                  {{ detailItem.active_provider_subscription_ready_count }} /
                  {{ detailItem.active_subscription_count }}
                  ({{
                    percent(
                      detailItem.active_provider_subscription_coverage_rate
                    )
                  }})
                </td>
              </tr>
              <tr>
                <th>Pending/Unknown</th>
                <td>
                  {{ detailItem.provider_pending_attempt_count }} /
                  {{ detailItem.provider_unknown_attempt_count }}
                </td>
              </tr>
              <tr>
                <th>Manual Review</th>
                <td>{{ detailItem.provider_manual_review_attempt_count }}</td>
              </tr>
              <tr>
                <th>Son Başarılı Ödeme</th>
                <td>
                  {{
                    detailItem.last_success_at
                      ? new Date(detailItem.last_success_at).toLocaleString(
                          "tr-TR"
                        )
                      : "-"
                  }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-alert
            v-if="detailItem.payment_readiness_warnings.length"
            type="warning"
            outlined
            dense
            class="mt-4"
          >
            <div
              v-for="warning in detailItem.payment_readiness_warnings"
              :key="warning"
            >
              {{ warningText(warning) }}
            </div>
          </v-alert>

          <div class="d-flex flex-wrap mt-4">
            <v-btn
              small
              color="primary"
              outlined
              class="mr-2 mb-2"
              :to="{
                name: 'definitions.clusters.edit',
                params: { id: detailItem.cluster_id },
                hash: '#cc',
              }"
            >
              TYA POS Ayarı
            </v-btn>

            <v-btn
              small
              color="primary"
              outlined
              class="mr-2 mb-2"
              :to="ccPaymentRoute(detailItem)"
            >
              POS İşlemleri
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-navigation-drawer>
  </v-card>
</template>

<script>
import { debounce } from "@/core/plugins/debounce";
import { filtersToURL, hasExport, hasPermissions, isPage } from "@/view/mixins";
import { mapGetters } from "vuex";

export default {
  name: "TyaReadiness",
  mixins: [filtersToURL, hasExport, hasPermissions, isPage],
  computed: {
    ...mapGetters(["paymentProviderList"]),
    totals() {
      const t = this.footTotals || {};

      return {
        itemCount: this.number(t.item_count),
        selectedProviderCount: this.number(t.selected_provider_count),
        recardRequiredUserCount: this.number(t.recard_required_user_count),
        subscriptionMissingCardCount: this.number(
          t.subscription_missing_card_count
        ),
        pendingUnknownAttemptCount:
          this.number(t.provider_pending_attempt_count) +
          this.number(t.provider_unknown_attempt_count),
        successPaymentAmount: this.number(t.success_payment_amount),
      };
    },
  },
  data() {
    return {
      pageMeta: {
        title: "TYA Ödeme Hazırlığı",
      },
      detailDrawer: false,
      detailItem: null,
      search: {
        query: null,
        has_selected_provider: null,
        provider_setup_status: null,
        payment_created_at: {
          min: null,
          max: null,
        },
      },
      options: {
        sortBy: ["cluster_name"],
        sortDesc: [false],
      },
      dataTableAttrs: {
        footerProps: {
          disableItemsPerPage: false,
        },
      },
      providerSetupStatusOptions: [
        { id: "draft", text: "Taslak" },
        { id: "pending", text: "Bekliyor" },
        { id: "ready", text: "Hazır" },
        { id: "needs_review", text: "İnceleme Gerekli" },
        { id: "failed", text: "Başarısız" },
        { id: "archived", text: "Arşiv" },
      ],
      titleBarAttrs: {
        exportUrl: () => "api-super-admin/payment-ops/tya-readiness",
        exportParams: this.getParams,
      },
      headers: [
        {
          text: "",
          value: "actions",
          searchable: false,
          sortable: false,
          width: "36px",
          align: "center",
          notPrice: true,
        },
        {
          text: "Durum",
          value: "payment_readiness_status",
          searchable: false,
          sortable: false,
          align: "center",
          width: "90px",
          notPrice: true,
        },
        {
          text: "TYA",
          value: "cluster_name",
          searchable: "text",
          searchValue: "query",
          width: "220px",
        },
        {
          text: "Yönetim Şirketi",
          value: "company_name",
          searchable: "text",
          width: "220px",
        },
        {
          text: "Provider",
          value: "payment_provider_name",
          searchable: "multiselect",
          searchValue: "payment_provider_ids",
          options: () => this.paymentProviderList,
          width: "140px",
        },
        {
          text: "Entegrasyon",
          value: "provider_setup_status",
          searchable: "select",
          options: () => this.providerSetupStatusOptions,
          itemText: "text",
          width: "140px",
          notPrice: true,
        },
        {
          text: "Sakin",
          value: "active_resident_count",
          searchable: false,
          align: "end",
          width: "80px",
          notPrice: true,
        },
        {
          text: "Kartlı Kullanıcı",
          value: "card_user_any_provider_count",
          searchable: false,
          align: "end",
          width: "110px",
          notPrice: true,
        },
        {
          text: "Aktif Provider Kart",
          value: "active_provider_card_user_count",
          searchable: false,
          align: "end",
          width: "120px",
          notPrice: true,
        },
        {
          text: "Kart Kapsam",
          value: "active_provider_card_coverage_rate",
          searchable: false,
          sortable: false,
          align: "end",
          width: "100px",
          notPrice: true,
        },
        {
          text: "Re-card",
          value: "recard_required_user_count",
          searchable: false,
          align: "end",
          width: "90px",
          notPrice: true,
        },
        {
          text: "Talimat",
          value: "active_subscription_count",
          searchable: false,
          align: "end",
          width: "90px",
          notPrice: true,
        },
        {
          text: "Talimat Hazır",
          value: "active_provider_subscription_ready_count",
          searchable: false,
          align: "end",
          width: "100px",
          notPrice: true,
        },
        {
          text: "Talimat Kapsam",
          value: "active_provider_subscription_coverage_rate",
          searchable: false,
          sortable: false,
          align: "end",
          width: "110px",
          notPrice: true,
        },
        {
          text: "Başarılı",
          value: "success_payment_count",
          searchable: false,
          align: "end",
          width: "90px",
          notPrice: true,
        },
        {
          text: "Başarılı Hacim",
          value: "success_payment_amount",
          searchable: false,
          align: "end",
          width: "120px",
        },
        {
          text: "Fail",
          value: "failed_payment_count",
          searchable: false,
          align: "end",
          width: "80px",
          notPrice: true,
        },
        {
          text: "Pending",
          value: "provider_pending_attempt_count",
          searchable: false,
          align: "end",
          width: "90px",
          notPrice: true,
        },
        {
          text: "Unknown",
          value: "provider_unknown_attempt_count",
          searchable: false,
          align: "end",
          width: "90px",
          notPrice: true,
        },
        {
          text: "Manual",
          value: "provider_manual_review_attempt_count",
          searchable: false,
          align: "end",
          width: "90px",
          notPrice: true,
        },
        {
          text: "Son Başarılı",
          value: "last_success_at",
          searchable: false,
          width: "150px",
          notPrice: true,
        },
      ],
    };
  },
  mounted() {
    this.generateExportColumns(
      this.headers.filter((header) => header.value !== "actions")
    );
    this.$nextTick(() => this.loadList());
  },
  methods: {
    getParams() {
      const params = Object.assign(
        this.buildSearchParams(this.search, this.options)
      );

      if (
        !this.search.payment_created_at?.min &&
        !this.search.payment_created_at?.max
      ) {
        delete params.payment_created_at;
        delete params["payment_created_at[min]"];
        delete params["payment_created_at[max]"];
      }

      if (this.search.has_selected_provider === null) {
        delete params.has_selected_provider;
      }

      return params;
    },
    loadList() {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;
      this.list = [];
      this.footTotals = [];

      this.$api
        .query("api-super-admin/payment-ops/tya-readiness", {
          params: this.getParams(),
        })
        .then((response) => {
          this.loadListFromResponse(response);
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => (this.isLoading = false));
    },
    handleClearFilters() {
      this.$refs.headSearch && this.$refs.headSearch.reset();
      this.$set(this.search, "has_selected_provider", null);
      this.$set(this.search, "provider_setup_status", null);
      this.$set(this.search, "payment_created_at", {
        min: null,
        max: null,
      });
    },
    showDetail(item) {
      this.detailItem = item;
      this.detailDrawer = true;
    },
    ccPaymentRoute(item, query = {}) {
      return {
        name: "activities.cc-payments.list",
        query: {
          "cluster_id[]": [item.cluster_id],
          ...query,
        },
      };
    },
    readinessStatusMeta(status) {
      const map = {
        ready: { text: "Hazır", color: "success" },
        warning: { text: "Uyarı", color: "warning" },
        blocked: { text: "Bloklu", color: "error" },
      };

      return map[status] || { text: "-", color: "grey" };
    },
    setupStatusText(status) {
      const item = this.providerSetupStatusOptions.find(
        (option) => option.id === status
      );

      return item ? item.text : status || "-";
    },
    readinessReasonText(reason) {
      const map = {
        provider_not_selected: "TYA için aktif ödeme sağlayıcısı seçilmemiş.",
        payment_globally_disabled:
          "Kartlı ödeme global olarak kapalı olduğu için yeni ödeme alınamaz.",
        adapter_not_supported:
          "Seçili sağlayıcı için sistemde desteklenen adapter yok.",
        provider_disabled:
          "Seçili sağlayıcı adapter/config seviyesinde kapalı.",
        provider_setup_not_ready:
          "TYA sağlayıcı kurulumu ödeme almaya hazır değil.",
        submerchant_missing:
          "Pazaryeri için gerekli alt üye işyeri bilgisi eksik.",
        payout_account_not_ready: "Hakediş banka hesabı veya IBAN eksik.",
        ready_with_warnings:
          "Ödeme alınabilir, ancak operasyonel takip gerektiren uyarılar var.",
        ready: "Ödeme almaya hazır görünüyor.",
      };

      return map[reason] || reason || "-";
    },
    warningText(warning) {
      const map = {
        recard_required:
          "Başka provider'da kartı olan ama aktif provider token'ı eksik kullanıcı var.",
        subscription_card_missing:
          "Otomatik tahsilatı olan bazı kullanıcıların aktif provider kartı yok.",
        pending_or_unknown_attempts:
          "Pending/unknown provider attempt bulundu, tekrar ödeme öncesi kontrol edilmeli.",
        manual_review_attempts:
          "Manuel inceleme gerektiren ödeme denemesi var.",
      };

      return map[warning] || warning;
    },
    percent(value) {
      if (value === null || value === undefined) {
        return "-";
      }

      return `${(Number(value) * 100).toFixed(1)} %`;
    },
    number(value) {
      return Number(value || 0);
    },
    getRowClass(item) {
      if (item.payment_readiness_status === "blocked") {
        return "payment-readiness-row--blocked";
      }

      if (item.payment_readiness_status === "warning") {
        return "payment-readiness-row--warning";
      }

      return "";
    },
  },
  watch: {
    options: {
      handler() {
        this.setURLParams();
      },
      deep: true,
    },
    search: {
      handler: debounce(function () {
        this.options.page = 1;
        this.setURLParams();
      }, 250),
      deep: true,
    },
  },
};
</script>

<style scoped>
::v-deep .payment-readiness-row--blocked {
  background: #fff5f5;
}

::v-deep .payment-readiness-row--warning {
  background: #fffaf0;
}
</style>
